// @ts-nocheck
// FP4 · Caderno de Estudos pessoal de um livro (book-study.tsx)
// Porte do design "Estudos do Livro" (Mobile-first design guidance) para dados reais.
// Espinha da leitura + cards por tipo (nota/grifo/ideia/pergunta/foto/áudio).
// Captura: Texto, Voz (MediaRecorder → upload), Foto (citação). Player de áudio
// com velocidade 1×/1,5×/2× (client-side, custo zero). Tudo PRIVADO (só o leitor).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/apiV2';
import type { AuthSession, BookV2 } from '../types';
import { unitMeta } from './units';
import { CameraCapture } from './camera-capture';
import { OverlayStoryModal, BotaoOverlayStory } from './overlay-story';
// O motor de áudio saiu daqui para um módulo próprio: ele não sabe o que é um
// livro, e o Estudo do clube precisa dele inteiro.
import { AudioPlayer, useVoiceRecorder, fmtTime, DEFAULT_WAVE } from './audio';
// TYPECOLOR entra aqui e nao so no `export ... from` abaixo: reexportar NAO
// cria binding local. Sem esta linha o arquivo referencia uma variavel que
// nao existe, e a tela do caderno do livro fica BRANCA no render — sem erro
// de build, porque este arquivo tem @ts-nocheck no topo.
import { NoteCard, TYPECOLOR, LABEL, COLORVAR, FILTERS, semAcento, makePageLabel } from './note-card';
import { NotaComposer, CampoDeCaptura, type ContextoDaNota } from './nota-composer';
import { ImgCapa } from './common';
import { TrilhaDoMentor } from './mentor-livro/trilha';
import { CartaoGancho, useGanchoDoCaderno } from './mentor-livro/gancho-caderno';

// O card e as constantes que ele usa moram em note-card.tsx desde 24/09/2026.
// Reexportados aqui porque a visao global e o roteador ja importavam daqui —
// trocar os dois lados de uma vez e o jeito de quebrar as duas telas juntas.
export { NoteCard, TYPECOLOR, LABEL, COLORVAR, FILTERS, semAcento, makePageLabel } from './note-card';
const PLACE: Record<string, string> = {
  nota: 'O que você quer guardar dessa leitura?',
  grifo: 'Digite o trecho que você grifou…',
  ideia: 'Qual ideia surgiu? Escreva pra não esquecer.',
  pergunta: 'O que você quer revisitar depois?',
};
const MODES = [{ key: 'texto', label: 'Texto' }, { key: 'voz', label: 'Voz' }, { key: 'foto', label: 'Foto' }];
const TYPES = [{ key: 'nota', label: 'Nota' }, { key: 'grifo', label: 'Grifo' }, { key: 'ideia', label: 'Ideia' }, { key: 'pergunta', label: 'Pergunta' }];
const DOTS = ['none', 'ember', 'sage', 'plum'];
// makePageLabel vive em note-card.tsx e agora conhece capitulos e posicoes.

export const BookStudyScreen: React.FC<{
  book: BookV2;
  session: AuthSession | null;
  onBack: () => void;
  autoComposePage?: number | null;
  /** A pergunta do insight que abriu o editor ("Responder no caderno"). */
  autoComposeContexto?: ContextoDaNota | null;
  /** Veio do aviso "nova nota do Mentor": abre já na Trilha do Mentor. */
  abrirNaTrilha?: boolean;
  onAbrirTravessia?: () => void;
  /** Interface nova: a chave Travessia | Caderno no topo, sobre a capa. */
  abas?: React.ReactNode;
}> = ({ book, session, onBack, autoComposePage, autoComposeContexto, abrirNaTrilha, onAbrirTravessia, abas }) => {
  const u = unitMeta(book.progressUnit);
  const pageLabel = makePageLabel(book.progressUnit);
  const currentPage = book.read || 0;
  const totalPages = book.pages || 0;

  const [notes, setNotes] = useState<any[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [filter, setFilter] = useState('tudo');
  // Abre em RECENTES, nao na espinha da leitura. O caderno se le de tras pra
  // frente: o que interessa ao abrir e o que foi anotado por ultimo. A ordem
  // por pagina serve para revisao — releitura do inicio, revisao antes de
  // terminar — e continua a um toque de distancia.
  const [sort, setSort] = useState<'pagina' | 'recente'>('recente');
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [justAddedId, setJustAddedId] = useState<string | null>(null);
  const justTimer = useRef<any>(null);

  // composer
  const [composerOpen, setComposerOpen] = useState(false);
  // CRIAR passou a usar o compositor unico (nota-composer.tsx), o mesmo do
  // caderno pessoal. A folha antiga daqui ficou so para EDITAR uma nota que
  // ja existe — sao dois trabalhos diferentes, e o compositor novo nao edita.
  const [novoAberto, setNovoAberto] = useState(false);
  const [novoModo, setNovoModo] = useState<'texto' | 'voz' | 'foto'>('texto');
  const [novoTexto, setNovoTexto] = useState('');
  const [novoContexto, setNovoContexto] = useState<ContextoDaNota | null>(null);
  const [editing, setEditing] = useState<any | null>(null);   // nota em edição (ou null = criar)
  const [mode, setMode] = useState<'texto' | 'voz' | 'foto'>('texto');
  const [draftType, setDraftType] = useState('nota');
  const [draftText, setDraftText] = useState('');
  const [draftPage, setDraftPage] = useState<number>(currentPage);
  const [hasPage, setHasPage] = useState(true);
  const [draftColor, setDraftColor] = useState('none');
  const [pageEditing, setPageEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [composerErr, setComposerErr] = useState<string | null>(null);
  // A nota mudou em outra aba/aparelho enquanto esta edição estava aberta
  // (pente fino, QA-22). Guarda a versão de lá; a edição daqui fica no editor.
  const [conflitoNota, setConflitoNota] = useState<any | null>(null);

  // foto
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // voz — o diagnóstico de microfone continua aqui porque só esta tela manda
  // a versão NATIVA do app junto (é o dado que diz se o build novo instalou).
  const voz = useVoiceRecorder(useCallback(async ({ name, message }) => {
    let appVer = 'web';
    try { const cap: any = await import('@capacitor/app'); const info = await cap.App.getInfo(); appVer = `${info.version}+${info.build}`; } catch (_) {}
    try { fetch('/api/debug-log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tag: 'mic-error', data: { name, message, appVer, ua: (navigator.userAgent || '').slice(0, 120) } }) }).catch(() => {}); } catch (_) {}
  }, []));
  const { stage: voiceStage, sec: recSec, err: recErr, blob: recBlob, url: recUrl, dur: recDur, wave: recWave } = voz;
  const photoPreviewRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    try { setLoadErr(null); const data = await api.listBookNotes(session, book.id); setNotes(data || []); }
    catch (e: any) { setLoadErr(e?.message || 'Erro ao carregar'); setNotes([]); }
  }, [session, book.id]);

  useEffect(() => { load(); }, [load]);

  // ref sempre apontando pra URL vigente (pra revogar no unmount sem stale closure)
  useEffect(() => { photoPreviewRef.current = photoPreview; }, [photoPreview]);

  // Do áudio quem cuida é o hook, inclusive no unmount. Aqui sobra a foto.
  useEffect(() => () => {
    if (photoPreviewRef.current) URL.revokeObjectURL(photoPreviewRef.current);
  }, []); // eslint-disable-line

  const resetComposerMedia = () => {
    voz.reset();
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null); setPhotoPreview(null);
  };

  const openComposer = (m: 'texto' | 'voz' | 'foto', page = currentPage, texto = '', contexto: ContextoDaNota | null = null) => {
    // Abre o compositor UNICO. `page` ainda entra porque o gancho "anote o
    // que voce leu" chega com a pagina do registro.
    setDraftPage(page);
    setNovoTexto(texto);
    setNovoContexto(contexto);
    setNovoModo(m);
    setNovoAberto(true);
  };

  const openComposerAntigo = (m: 'texto' | 'voz' | 'foto', page = currentPage) => {
    resetComposerMedia();
    setEditing(null); setComposerOpen(true); setMode(m); setDraftType('nota'); setDraftText('');
    setDraftPage(page); setHasPage(true); setDraftColor('none'); setPageEditing(false); setComposerErr(null);
  };
  const openEdit = (n: any) => {
    resetComposerMedia();
    setEditing(n); setComposerOpen(true);
    setMode(n.audio ? 'voz' : n.photo ? 'foto' : 'texto');
    setDraftType(n.type || 'nota'); setDraftText(n.text || '');
    setDraftPage(n.page ?? currentPage); setHasPage(n.page !== null && n.page !== undefined);
    setDraftColor(n.color || 'none'); setPageEditing(false); setComposerErr(null);
    if (n.audio) voz.setStage('recorded');
  };
  const closeComposer = () => { setComposerOpen(false); resetComposerMedia(); setConflitoNota(null); };

  const startRec = voz.start;
  const stopRec = voz.stop;
  const reRecord = voz.regravar;

  // ── foto ───────────────────────────────────────────────────────────────
  const acceptPhoto = (f: File) => {
    if (!f) return;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(f); setPhotoPreview(URL.createObjectURL(f)); setDraftType('grifo');
    setCameraOpen(false);
  };

  // ── salvar ───────────────────────────────────────────────────────────────
  const flashAdded = (id: string) => { setJustAddedId(id); clearTimeout(justTimer.current); justTimer.current = setTimeout(() => setJustAddedId(null), 2400); };

  const handleSave = async () => {
    if (saving) return;
    const page = hasPage ? draftPage : null;
    const text = (draftText || '').trim();
    try {
      setSaving(true); setComposerErr(null);
      if (editing) {
        // Manda a versão de que partiu: sem ela, a edição de outra aba sumia
        // sem aviso (pente fino, QA-22 — a mesma regra da folha, DEV-11).
        try {
          const updated = await api.updateBookNote(session, editing.id, { text: draftText, page, color: draftColor, ...(editing.updatedAt ? { seAtualizadoEm: editing.updatedAt } : {}) });
          setNotes(prev => (prev || []).map(n => n.id === editing.id ? updated : n));
          setConflitoNota(null);
          closeComposer(); return;
        } catch (e: any) {
          if (e?.status === 409 && e?.dados?.atual) {
            const atual = e.dados.atual;
            setNotes(prev => (prev || []).map(n => n.id === editing.id ? atual : n));
            setConflitoNota(atual);
            setSaving(false);
            return;
          }
          throw e;
        }
      }
      if (mode === 'voz') {
        if (!recBlob) { setComposerErr('Grave um áudio primeiro.'); setSaving(false); return; }
        const filename = voz.nomeDoArquivo('nota');
        const up = await api.uploadBookNoteAudio(session, recBlob, filename);
        const created = await api.createBookNote(session, book.id, { type: 'audio', text, page, color: draftColor, audioUrl: up.url, audioDur: recDur, wave: recWave || DEFAULT_WAVE });
        setNotes(prev => [...(prev || []), created]); flashAdded(created.id); closeComposer(); return;
      }
      if (mode === 'foto') {
        if (!photoFile) { setComposerErr('Tire ou escolha uma foto primeiro.'); setSaving(false); return; }
        const up = await api.uploadBookNotePhoto(session, photoFile);
        const created = await api.createBookNote(session, book.id, { type: draftType === 'audio' ? 'grifo' : draftType, text, page, color: draftColor, photoUrl: up.url, photoKind: 'page' });
        setNotes(prev => [...(prev || []), created]); flashAdded(created.id); closeComposer(); return;
      }
      // texto
      if (!text) { setComposerErr('Escreva algo pra guardar.'); setSaving(false); return; }
      const created = await api.createBookNote(session, book.id, { type: draftType, text, page, color: draftColor });
      setNotes(prev => [...(prev || []), created]); flashAdded(created.id); closeComposer();
    } catch (e: any) {
      setComposerErr(e?.message || 'Erro ao guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm('Excluir esta anotação? Não dá pra desfazer.')) return;
    try { setSaving(true); await api.deleteBookNote(session, editing.id); setNotes(prev => (prev || []).filter(n => n.id !== editing.id)); closeComposer(); }
    catch (e: any) { setComposerErr(e?.message || 'Erro ao excluir'); } finally { setSaving(false); }
  };

  // abertura automática (vindo do "Anote o que você leu" pós-registro) — captura o valor
  // no MOUNT e abre o composer uma vez, independente do fetch das notas e de re-sync de URL.
  const initialComposeRef = useRef(autoComposePage);
  const initialContextoRef = useRef(autoComposeContexto);
  useEffect(() => {
    if (initialComposeRef.current != null) openComposer('texto', initialComposeRef.current as number, '', initialContextoRef.current || null);
  }, []); // eslint-disable-line

  // foca o textarea (abre o teclado) SÓ quando o composer abre em modo Texto — nunca no load da página
  useEffect(() => {
    if (composerOpen && mode === 'texto') {
      const t = setTimeout(() => { try { textAreaRef.current?.focus(); } catch (_) {} }, 130);
      return () => clearTimeout(t);
    }
  }, [composerOpen, mode]);

  // ── derivar lista visível ────────────────────────────────────────────────
  const all = notes || [];
  const ql = semAcento(search);
  let list = all.filter(n => (filter === 'tudo' ? true : filter === 'foto' ? !!n.photo : n.type === filter))
    .filter(n => !ql || semAcento(n.text).includes(ql));
  list = list.slice().sort((a, b) => sort === 'pagina'
    ? (((a.page ?? 9999999) - (b.page ?? 9999999)) || (String(a.createdAt).localeCompare(String(b.createdAt))))
    : String(b.createdAt).localeCompare(String(a.createdAt)));

  const showMarker = sort === 'pagina' && filter === 'tudo' && !ql && list.length > 0;
  const items: any[] = list.map(n => ({ ...n }));
  if (showMarker) {
    const marker = { __marker: true, id: '__marker', page: currentPage };
    // 1º item paginado à frente da posição atual; se não houver, antes do bloco de avulsas (page null)
    let idx = items.findIndex(it => it.page != null && it.page > currentPage);
    if (idx === -1) idx = items.findIndex(it => it.page == null);
    if (idx === -1) items.push(marker); else items.splice(idx, 0, marker);
  }

  const isEmpty = list.length === 0;
  // Interface nova (a chave no topo = livro com Travessia): o caderno em branco
  // traz a pergunta do último insight liberado, no lugar do aviso genérico.
  const gancho = useGanchoDoCaderno(session, book.id, !!abas && notes !== null && all.length === 0);
  let emptyTitle = 'Seu caderno desta leitura está em branco.';
  let emptySub = 'Anote o que você acabou de ler — uma frase basta — para reter o que importa.';
  if (isEmpty && ql) { emptyTitle = 'Nada encontrado.'; emptySub = 'Tente outras palavras ou limpe a busca.'; }
  else if (isEmpty && filter !== 'tudo') {
    const fl = (FILTERS.find(f => f.key === filter) || {}).label || '';
    emptyTitle = `Nenhum item em '${fl}' ainda.`; emptySub = 'Que tal guardar o primeiro agora mesmo?';
  }

  const progressPct = totalPages > 0 ? Math.round(currentPage / totalPages * 100) : 0;
  const coverHue = (book as any).coverHue ?? 22;
  const showFooter = !((mode === 'foto' && !photoPreview && !editing) || (mode === 'voz' && voiceStage !== 'recorded' && !editing));

  return (
    // A mesma coluna do resto do app no computador: o caderno ocupava 1280 px,
    // com o campo de anotar de ponta a ponta (pente fino, QA-14).
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'var(--paper)', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxWidth: 480, margin: '0 auto' }}>
      <style>{`@keyframes bsPulse{0%,100%{box-shadow:0 0 0 0 rgba(232,122,59,0.45)}50%{box-shadow:0 0 0 14px rgba(232,122,59,0)}}@keyframes bsDrop{0%{transform:translateY(-10px) rotate(-3deg);opacity:0}100%{transform:translateY(0) rotate(-1.6deg);opacity:1}}@keyframes bsSpin{to{transform:rotate(360deg)}}`}</style>
      {/* SCROLLER */}
      <div className="om-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* COVER / CLIMA */}
        <div style={{ position: 'relative', height: 224, overflow: 'hidden' }}>
          {book.coverUrl
            ? <img src={book.coverUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(18px) saturate(1.1)', transform: 'scale(1.2)' }} />
            : <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(120% 90% at 78% 18%, oklch(0.55 0.12 ${coverHue}), oklch(0.32 0.08 ${coverHue}) 55%, #1c130d)` }} />}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 38%, rgba(10,7,5,0.6) 78%, var(--paper) 100%)' }} />
          {/* back (no lugar do antigo seletor de tema) */}
          <div style={{ position: 'absolute', top: 'calc(12px + env(safe-area-inset-top, 0px))', left: 16, right: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 3 }}>
            <button onClick={onBack} aria-label="Voltar" style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(20,14,10,0.42)', backdropFilter: 'blur(8px)', color: '#F3ECDE', cursor: 'pointer' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            {abas}
            {abas && <span style={{ width: 38, flex: 'none' }} />}
          </div>
          {/* identidade do livro */}
          <div style={{ position: 'absolute', left: 20, right: 20, bottom: 16, zIndex: 2, display: 'flex', alignItems: 'flex-end', gap: 14 }}>
            <div style={{ width: 60, height: 88, flex: 'none', borderRadius: 4, overflow: 'hidden', boxShadow: '0 10px 26px rgba(0,0,0,.5)', background: `linear-gradient(155deg, oklch(0.78 0.09 ${coverHue}), oklch(0.45 0.10 ${coverHue}))`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6, textAlign: 'center' }}>
              <ImgCapa src={book.coverUrl || undefined} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                fallback={<div className="serif" style={{ fontSize: 12, lineHeight: 1.05, color: '#241405' }}>{book.title}</div>} />
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 2 }}>
              <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.15em', color: 'rgba(243,236,222,0.7)', marginBottom: 5 }}>CADERNO DE ESTUDOS</div>
              <div className="serif" style={{ fontSize: 25, lineHeight: 0.99, color: '#F8F2E6', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{book.title}</div>
              <div style={{ fontSize: 12, color: 'rgba(243,236,222,0.72)', marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.author}</div>
            </div>
          </div>
        </div>

        {/* progresso */}
        <div style={{ padding: '14px 20px 6px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, height: 3, borderRadius: 3, background: 'var(--line-strong)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progressPct}%`, background: 'var(--ember)', borderRadius: 3 }} />
          </div>
          <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 10.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{pageLabel(currentPage)} de {totalPages}</div>
        </div>
        <div style={{ padding: '0 20px', fontSize: 12.5, color: 'var(--ink-4)' }}>
          {all.length > 0 ? `${all.length} ${all.length === 1 ? 'anotação guardada' : 'anotações guardadas'} desta leitura` : 'Nenhuma anotação ainda'}
        </div>

        {/* MENTOR DO LIVRO — a trilha liberada pela página, separada das
            notas da pessoa. Desligada (MENTOR_LIVRO_ATIVO) não desenha nada. */}
        {/* Com a chave Travessia | Caderno no topo, o atalho em cartão seria a mesma porta duas vezes. */}
        {!abas && <TrilhaDoMentor session={session} bookId={book.id} focar={abrirNaTrilha} onAbrirTravessia={onAbrirTravessia} />}

        {/* sticky: busca / chips / sort */}
        <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'color-mix(in srgb, var(--paper) 88%, transparent)', backdropFilter: 'blur(12px)', padding: '12px 0 11px', marginTop: 12, borderBottom: '1px solid var(--line)' }}>
          {searchOpen ? (
            <div style={{ padding: '0 20px', display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, padding: '9px 13px', background: 'var(--paper-2)', border: '1px solid var(--line-strong)', borderRadius: 12 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth={1.7} strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></svg>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar nas anotações…" autoFocus style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit' }} />
              </div>
              <button onClick={() => { setSearchOpen(false); setSearch(''); }} style={{ border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 14, cursor: 'pointer' }}>Fechar</button>
            </div>
          ) : (
            <div className="om-scroll" style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto', padding: '0 20px' }}>
              <button onClick={() => setSearchOpen(true)} aria-label="Buscar" style={{ flex: 'none', width: 36, height: 32, borderRadius: 10, border: '1px solid var(--line-strong)', background: 'transparent', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></svg>
              </button>
              {FILTERS.map(f => {
                const a = filter === f.key;
                return <button key={f.key} onClick={() => setFilter(f.key)} style={{ flex: 'none', height: 32, padding: '0 14px', borderRadius: 10, border: a ? '1px solid transparent' : '1px solid var(--line-strong)', background: a ? 'color-mix(in srgb, var(--ink) 12%, transparent)' : 'transparent', color: a ? 'var(--ink)' : 'var(--ink-3)', fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>{f.label}</button>;
              })}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '11px 20px 0' }}>
            <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.12em', color: 'var(--ink-4)' }}>VER POR</span>
            <button onClick={() => setSort('pagina')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'Geist Mono, monospace', fontSize: 11, letterSpacing: '.05em', color: sort === 'pagina' ? 'var(--ink)' : 'var(--ink-4)', padding: 0 }}>ESPINHA DA LEITURA</button>
            <button onClick={() => setSort('recente')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'Geist Mono, monospace', fontSize: 11, letterSpacing: '.05em', color: sort === 'recente' ? 'var(--ink)' : 'var(--ink-4)', padding: 0 }}>RECENTES</button>
          </div>
        </div>

        {/* FEED */}
        <div style={{ padding: '18px 18px 150px' }}>
          {notes === null && <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>Carregando…</div>}
          {loadErr && <div style={{ padding: 20, textAlign: 'center', color: 'var(--ember)', fontSize: 13 }}>{loadErr}</div>}

          {notes !== null && all.length === 0 && gancho && (
            <CartaoGancho gancho={gancho} onResponder={(p, c) => openComposer('texto', p, '', c)} style={{ margin: '6px 2px 0' }} />
          )}

          {notes !== null && isEmpty && !(all.length === 0 && gancho) && (
            <div style={{ padding: '40px 24px 30px', textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, margin: '0 auto 22px', borderRadius: '50%', border: '1.5px dashed var(--line-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-4)' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round"><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z" /><path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5A1.5 1.5 0 0 0 20 18.5z" /></svg>
              </div>
              <div className="serif" style={{ fontSize: 25, lineHeight: 1.12, color: 'var(--ink)', maxWidth: 280, margin: '0 auto 12px' }}>{emptyTitle}</div>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-3)', maxWidth: 266, margin: '0 auto 26px' }}>{emptySub}</div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: 'var(--ink-4)' }}>
                <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 10, letterSpacing: '0.13em' }}>COMECE AQUI EMBAIXO</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ember)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><polyline points="6 13 12 19 18 13" /></svg>
              </div>
            </div>
          )}

          {items.map((n) => {
            const tc = n.__marker ? 'var(--ember)' : (TYPECOLOR[n.type] || 'var(--ink-3)');
            return (
              <div key={n.id} style={{ display: 'flex', alignItems: 'stretch' }}>
                {/* A ESPINHA — fina, encostada na margem.
                    Tinha 50px de largura para caber o "p. 34" embaixo do
                    marcador, e aquele rótulo ficava colado na linha vertical,
                    ilegível. A página foi para o rodapé da nota, junto do tipo
                    e da data; sem ela a espinha é só o que precisa ser: um fio
                    e um ponto. De 50px para 20 — 30px a mais de texto em cada
                    linha, numa tela de 430. */}
                <div style={{ position: 'relative', width: 20, flex: 'none' }}>
                  <div style={{ position: 'absolute', left: 6, top: 0, bottom: 0, width: 1.5, background: 'var(--line)' }} />
                  <div style={{ position: 'absolute', left: 1, top: 4, width: 12, height: 12, borderRadius: '50%', background: 'var(--paper)', border: `2px solid ${tc}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 3.5, height: 3.5, borderRadius: '50%', background: tc }} />
                  </div>
                </div>
                {/* card */}
                <div style={{ flex: 1, minWidth: 0, paddingBottom: 22, position: 'relative' }}>
                  {n.__marker ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0 9px' }}>
                      <div style={{ flex: 'none', fontFamily: 'Geist Mono, monospace', fontSize: 10, letterSpacing: '0.14em', color: 'var(--ember)' }}>VOCÊ LÊ AQUI</div>
                      <div style={{ flex: 1, height: 1, background: 'repeating-linear-gradient(90deg,var(--ember) 0 6px,transparent 6px 12px)', opacity: 0.6 }} />
                    </div>
                  ) : (
                    <NoteCard
                      n={n}
                      justAdded={justAddedId === n.id}
                      onEdit={() => openEdit(n)}
                      pageLabel={pageLabel}
                      livro={{ titulo: book.title, autor: book.author, capaUrl: (book as any).coverUrl || null }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* BOTTOM CAPTURE BAR */}
      <div style={{ flex: 'none', padding: '14px 18px calc(16px + env(safe-area-inset-bottom, 0px))', background: 'linear-gradient(transparent, var(--paper) 38%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 8px 8px 18px', background: 'var(--paper-2)', border: '1px solid var(--line-strong)', borderRadius: 30, boxShadow: isEmpty ? '0 0 0 2px var(--ember), 0 12px 30px rgba(0,0,0,.3)' : '0 12px 30px rgba(0,0,0,.3)' }}>
          {/* Campo de verdade (pente fino, UX-07): tocar abre o editor com o
              foco no texto, e o que já foi digitado vai junto. */}
          <CampoDeCaptura onAbrir={(t) => openComposer('texto', currentPage, t)} style={{ flex: 1, textAlign: 'left', border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 15, padding: '8px 0' }} />
          <button onClick={() => openComposer('voz')} aria-label="Gravar voz" style={{ width: 42, height: 42, flex: 'none', borderRadius: '50%', border: '1px solid var(--line-strong)', background: 'var(--paper)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2.5" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><line x1="12" y1="18" x2="12" y2="21.5" /><line x1="8.5" y1="21.5" x2="15.5" y2="21.5" /></svg>
          </button>
          <button onClick={() => openComposer('foto')} aria-label="Foto" style={{ width: 42, height: 42, flex: 'none', borderRadius: '50%', border: '1px solid var(--line-strong)', background: 'var(--paper)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5a2 2 0 0 1 2-2h2l1.2-1.8h5.6L17 6.5h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><circle cx="12" cy="13" r="3.3" /></svg>
          </button>
          <button onClick={() => openComposer('texto')} aria-label="Adicionar" style={{ width: 48, height: 48, flex: 'none', borderRadius: '50%', border: 'none', background: 'var(--ember)', color: '#1c1208', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 6px 16px rgba(232,122,59,.4)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
        </div>
      </div>

      {/* O COMPOSITOR UNICO — o mesmo do caderno pessoal. */}
      <NotaComposer
        session={session}
        livro={{
          id: book.id,
          titulo: book.title,
          // A página pedida por quem abriu (o insight, o registro); senão, onde ela está.
          paginaAtual: draftPage,
          totalPaginas: book.totalPages || undefined,
        }}
        pageLabel={pageLabel}
        aberto={novoAberto}
        modoInicial={novoModo}
        textoInicial={novoTexto}
        contexto={novoContexto}
        onFechar={() => setNovoAberto(false)}
        onCriada={() => { load(); }}
      />

      {/* FOLHA ANTIGA — so para EDITAR */}
      <div onClick={closeComposer} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(8,5,3,0.55)', backdropFilter: 'blur(2px)', transition: 'opacity .3s', opacity: composerOpen && editing ? 1 : 0, pointerEvents: composerOpen && editing ? 'auto' : 'none' }} />
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 61, background: 'var(--paper-2)', borderRadius: '26px 26px 0 0', boxShadow: '0 -16px 50px rgba(0,0,0,.4)', transform: composerOpen && editing ? 'translateY(0)' : 'translateY(105%)', transition: 'transform .42s cubic-bezier(.32,.72,.3,1)', maxHeight: '90%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 0 4px', display: 'flex', justifyContent: 'center', flex: 'none' }}><div style={{ width: 38, height: 4, borderRadius: 4, background: 'var(--line-strong)' }} /></div>
        <div style={{ padding: '6px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 'none' }}>
          <div className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>{editing ? 'Editar' : 'Anotar'}</div>
          <button onClick={closeComposer} aria-label="Fechar" style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'var(--paper-3)', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
          </button>
        </div>

        {/* abas de modo (só na criação) */}
        {!editing && (
          <div style={{ display: 'flex', gap: 7, padding: '14px 20px 0', flex: 'none' }}>
            {MODES.map(m => { const a = mode === m.key; return (
              <button key={m.key} onClick={() => { resetComposerMedia(); setMode(m.key as any); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 0', borderRadius: 12, border: a ? '1px solid var(--ink)' : '1px solid var(--line-strong)', background: a ? 'var(--ink)' : 'transparent', color: a ? 'var(--paper)' : 'var(--ink-3)', fontSize: 13.5, fontWeight: 500, cursor: 'pointer' }}>{m.label}</button>
            ); })}
          </div>
        )}

        <div className="om-scroll" style={{ overflowY: 'auto', padding: '16px 20px 0', flex: 1, minHeight: 0 }}>
          {composerErr && <div style={{ padding: '9px 12px', borderRadius: 10, marginBottom: 12, background: 'rgba(232,122,59,0.12)', color: 'var(--ember)', fontSize: 13 }}>{composerErr}</div>}
          {conflitoNota && editing && (
            <div role="alert" data-conflito-nota="1" style={{ padding: '12px 14px', borderRadius: 12, marginBottom: 12, background: 'rgba(232,122,59,0.10)', border: '1px solid rgba(232,122,59,0.35)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Esta nota mudou em outro aparelho.</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.45 }}>
                A sua edição continua aqui. A versão de lá: <em style={{ color: 'var(--ink-2)' }}>“{String(conflitoNota.text || '').slice(0, 140)}{String(conflitoNota.text || '').length > 140 ? '…' : ''}”</em>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button disabled={saving} onClick={async () => {
                  setSaving(true);
                  try {
                    const page = hasPage ? draftPage : null;
                    const updated = await api.updateBookNote(session, editing.id, { text: draftText, page, color: draftColor, seAtualizadoEm: conflitoNota.updatedAt });
                    setNotes(prev => (prev || []).map(n => n.id === editing.id ? updated : n));
                    setConflitoNota(null); closeComposer();
                  } catch (e: any) { setComposerErr(e?.message || 'Não deu para guardar.'); }
                  finally { setSaving(false); }
                }} style={{ padding: '8px 12px', borderRadius: 10, background: 'var(--ink)', color: 'var(--paper)', fontSize: 12.5, fontWeight: 600 }}>Guardar a minha por cima</button>
                <button disabled={saving} onClick={async () => {
                  setSaving(true);
                  try {
                    const page = hasPage ? draftPage : null;
                    const created = await api.createBookNote(session, book.id, { type: editing.type || 'nota', text: draftText, page, color: draftColor });
                    setNotes(prev => [...(prev || []), created]); flashAdded(created.id);
                    setConflitoNota(null); closeComposer();
                  } catch (e: any) { setComposerErr(e?.message || 'Não deu para guardar.'); }
                  finally { setSaving(false); }
                }} style={{ padding: '8px 12px', borderRadius: 10, background: 'transparent', border: '1px solid var(--line-strong)', color: 'var(--ink-2)', fontSize: 12.5, fontWeight: 600 }}>Guardar como nota nova</button>
              </div>
            </div>
          )}

          {/* type chips (oculto no modo Voz) */}
          {mode !== 'voz' && !editing && (
            <div className="om-scroll" style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 14 }}>
              {TYPES.map(t => { const a = draftType === t.key; const c = TYPECOLOR[t.key]; return (
                <button key={t.key} onClick={() => setDraftType(t.key)} style={{ flex: 'none', padding: '7px 14px', borderRadius: 20, border: `1px solid ${a ? c : 'var(--line-strong)'}`, background: a ? `color-mix(in srgb, ${c} 18%, transparent)` : 'transparent', color: a ? c : 'var(--ink-3)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>{t.label}</button>
              ); })}
            </div>
          )}

          {/* TEXTO */}
          {mode === 'texto' && (
            <textarea ref={textAreaRef} value={draftText} onChange={e => setDraftText(e.target.value)} placeholder={PLACE[draftType] || PLACE.nota} style={{ width: '100%', minHeight: 128, border: 'none', background: 'transparent', outline: 'none', resize: 'none', color: 'var(--ink)', fontFamily: 'Instrument Serif, serif', fontSize: 21, lineHeight: 1.4 }} />
          )}

          {/* VOZ */}
          {mode === 'voz' && (
            <>
              {(editing && editing.audio) ? (
                <>
                  <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.14em', color: 'var(--ember)', marginBottom: 10 }}>SUA NOTA DE VOZ</div>
                  <AudioPlayer url={editing.audioUrl} dur={editing.audioDur || 1} wave={editing.wave} />
                  <textarea value={draftText} onChange={e => setDraftText(e.target.value)} placeholder="Sobre o que é esse áudio? — pra você lembrar sem precisar reouvir" style={{ width: '100%', minHeight: 74, marginTop: 14, border: '1px solid var(--line)', background: 'var(--paper)', borderRadius: 12, padding: '12px 14px', outline: 'none', resize: 'none', color: 'var(--ink)', fontFamily: 'Instrument Serif, serif', fontSize: 18, lineHeight: 1.4 }} />
                </>
              ) : voiceStage !== 'recorded' ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 0 8px', minHeight: 170, justifyContent: 'center' }}>
                  <button onClick={() => voiceStage === 'recording' ? stopRec() : startRec()} style={{ width: 84, height: 84, borderRadius: '50%', border: 'none', background: 'var(--ember)', color: '#1c1208', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', animation: voiceStage === 'recording' ? 'bsPulse 1.5s ease-out infinite' : 'none' }}>
                    {voiceStage === 'recording'
                      ? <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2.5" /></svg>
                      : <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2.5" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><line x1="12" y1="18" x2="12" y2="21.5" /><line x1="8.5" y1="21.5" x2="15.5" y2="21.5" /></svg>}
                  </button>
                  {voiceStage === 'recording' && (
                    <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 25, color: 'var(--ink)', letterSpacing: '0.06em', margin: '18px 0 10px' }}>{fmtTime(recSec)}</div>
                  )}
                  <div style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 12, textAlign: 'center' }}>
                    {recErr || (voiceStage === 'recording' ? 'Gravando… toque para parar' : 'Toque para gravar sua nota de voz')}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.14em', color: 'var(--ember)', marginBottom: 10 }}>SUA NOTA DE VOZ</div>
                  {recUrl && <AudioPlayer url={recUrl} dur={recDur} wave={recWave} />}
                  <button onClick={reRecord} style={{ alignSelf: 'flex-start', marginTop: 12, border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>Regravar</button>
                  <textarea value={draftText} onChange={e => setDraftText(e.target.value)} placeholder="Sobre o que é esse áudio? — pra você lembrar sem precisar reouvir" style={{ width: '100%', minHeight: 74, marginTop: 14, border: '1px solid var(--line)', background: 'var(--paper)', borderRadius: 12, padding: '12px 14px', outline: 'none', resize: 'none', color: 'var(--ink)', fontFamily: 'Instrument Serif, serif', fontSize: 18, lineHeight: 1.4 }} />
                </div>
              )}
            </>
          )}

          {/* FOTO */}
          {mode === 'foto' && (
            <>
              {(editing && editing.photo) ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ background: '#f4efe4', padding: '9px 9px 14px', borderRadius: 3, boxShadow: '0 16px 36px rgba(0,0,0,.4)', transform: 'rotate(-2deg)', width: 212 }}>
                    <img src={editing.photoUrl} alt="" style={{ width: '100%', borderRadius: 1, display: 'block' }} />
                  </div>
                  <textarea value={draftText} onChange={e => setDraftText(e.target.value)} placeholder="Uma legenda curta: o que esse trecho mexeu em você?" style={{ width: '100%', minHeight: 74, marginTop: 16, border: '1px solid var(--line)', background: 'var(--paper)', borderRadius: 12, padding: '12px 14px', outline: 'none', resize: 'none', color: 'var(--ink)', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: 17, lineHeight: 1.4 }} />
                </div>
              ) : !photoPreview ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div onClick={() => setCameraOpen(true)} style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', background: '#1a120b', height: 280, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <div style={{ position: 'absolute', inset: 14, border: '2px dashed rgba(255,255,255,.28)', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: 'rgba(255,255,255,.85)' }}>
                      <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5a2 2 0 0 1 2-2h2l1.2-1.8h5.6L17 6.5h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><circle cx="12" cy="13" r="3.3" /></svg>
                      <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 10, letterSpacing: '0.08em', textAlign: 'center', padding: '0 20px' }}>FOTOGRAFE O TRECHO — A FOTO É A CITAÇÃO</div>
                    </div>
                  </div>
                  <button onClick={() => setCameraOpen(true)} style={{ marginTop: 18, padding: '12px 24px', borderRadius: 14, border: 'none', background: 'var(--ember)', color: '#1c1208', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Abrir câmera</button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ background: '#f4efe4', padding: '9px 9px 14px', borderRadius: 3, boxShadow: '0 16px 36px rgba(0,0,0,.4)', transform: 'rotate(-2deg)', width: 212 }}>
                    <img src={photoPreview} alt="" style={{ width: '100%', borderRadius: 1, display: 'block' }} />
                  </div>
                  <button onClick={() => setCameraOpen(true)} style={{ marginTop: 12, border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>Refazer foto</button>
                  <textarea value={draftText} onChange={e => setDraftText(e.target.value)} placeholder="Uma legenda curta: o que esse trecho mexeu em você?" style={{ width: '100%', minHeight: 74, marginTop: 14, border: '1px solid var(--line)', background: 'var(--paper)', borderRadius: 12, padding: '12px 14px', outline: 'none', resize: 'none', color: 'var(--ink)', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: 17, lineHeight: 1.4 }} />
                </div>
              )}
            </>
          )}

          {/* âncora de página + cor */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => { setPageEditing(v => !v); setHasPage(true); }} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 12px', borderRadius: 11, border: `1px solid ${hasPage ? 'var(--ember)' : 'var(--line-strong)'}`, background: hasPage ? 'color-mix(in srgb, var(--ember) 14%, transparent)' : 'transparent', color: hasPage ? 'var(--ember)' : 'var(--ink-3)', fontSize: 13, cursor: 'pointer' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z" /><path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5A1.5 1.5 0 0 0 20 18.5z" /></svg>
                <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 12 }}>{hasPage ? pageLabel(draftPage) : 'sem página'}</span>
              </button>
              <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>{hasPage && draftPage === currentPage ? 'sugerido — você está aqui' : 'toque para ajustar'}</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                {DOTS.map(c => { const a = draftColor === c; const fill = c === 'none' ? 'transparent' : COLORVAR[c]; return (
                  <button key={c} onClick={() => setDraftColor(c)} aria-label="cor" style={{ width: 22, height: 22, borderRadius: '50%', background: fill, border: `2px solid ${a ? 'var(--ink)' : (c === 'none' ? 'var(--ink-4)' : fill)}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {c === 'none' && <span style={{ width: 9, height: 1.5, background: 'var(--ink-4)' }} />}
                  </button>
                ); })}
              </div>
            </div>
            {pageEditing && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 13, padding: '11px 14px', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 12 }}>
                <button onClick={() => { setDraftPage(p => Math.max(0, (p || 0) - 1)); setHasPage(true); }} style={{ width: 34, height: 34, borderRadius: 9, border: '1px solid var(--line-strong)', background: 'var(--paper-2)', color: 'var(--ink)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>−</button>
                <div style={{ flex: 1, textAlign: 'center' }}><div className="serif" style={{ fontSize: 26, color: 'var(--ink)' }}>{hasPage ? pageLabel(draftPage) : 'sem página'}</div></div>
                <button onClick={() => { setDraftPage(p => Math.min(totalPages || 1000000, (p || 0) + 1)); setHasPage(true); }} style={{ width: 34, height: 34, borderRadius: 9, border: '1px solid var(--line-strong)', background: 'var(--paper-2)', color: 'var(--ink)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>+</button>
                <button onClick={() => setHasPage(v => !v)} style={{ border: 'none', background: 'transparent', color: hasPage ? 'var(--ink-3)' : 'var(--ember)', fontFamily: 'Geist Mono, monospace', fontSize: 10, letterSpacing: '0.05em', cursor: 'pointer' }}>SEM<br />PÁGINA</button>
              </div>
            )}
          </div>
          <div style={{ height: 14 }} />
        </div>

        {/* footer */}
        {showFooter && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 20px calc(20px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--line)', flex: 'none' }}>
            {editing
              ? <button onClick={handleDelete} disabled={saving} style={{ border: 'none', background: 'transparent', color: 'var(--ember)', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 }}>Excluir</button>
              : <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>Salvo no seu caderno</span>}
            <button onClick={handleSave} disabled={saving} style={{ padding: '12px 26px', borderRadius: 14, border: 'none', background: 'var(--ember)', color: '#1c1208', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.75 : 1, boxShadow: '0 6px 16px rgba(232,122,59,.35)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>{saving && <span style={{ width: 14, height: 14, border: '2px solid rgba(28,18,8,0.35)', borderTopColor: '#1c1208', borderRadius: '50%', animation: 'bsSpin .7s linear infinite' }} />}{saving ? 'Guardando…' : 'Guardar'}</button>
          </div>
        )}
      </div>

      {/* câmera in-app (Instagram-style) — universal, sem abrir a câmera nativa do SO */}
      <CameraCapture open={cameraOpen} title="Fotografe o trecho" onClose={() => setCameraOpen(false)} onCapture={acceptPhoto} />
    </div>
  );
};

// ── Card de uma anotação ────────────────────────────────────────────────────
